import React, { lazy, useState } from 'react';
import Stack from '@mui/material/Stack/Stack';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Alert from '@mui/material/Alert/Alert';
import Typography from '@mui/material/Typography/Typography';
import { Link } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { User } from '../../services/auth.service';
import { createIdea, IdeaOptions } from '../../services/idea.service';
import { HelpLink } from '../HelpLink';

const Editor = lazy(() => import('../Editor/Editor'));

export const NewIdea = ({
    hackathonId,
    hackathonSlug,
    template,
    onBehalfOptions,
    onCreate,
    allowance,
    upgradeUrl
}: {
    hackathonId: string,
    hackathonSlug: string,
    template?: string,
    // organizers: the participants they can pitch for (the idea's author becomes that person)
    onBehalfOptions?: Pick<User, 'email' | 'username'>[],
    onCreate: (ideaId: string) => void,
    // the Free plan's idea cap (null on Pro), shown before anyone hits it
    allowance?: { used: number, limit: number } | null,
    // organizers: where to upgrade the event
    upgradeUrl?: string
}) => {

    const [idea, setIdea] = useState<IdeaOptions>({
        title: '',
        description: template ?? ''
    })

    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // the ideas pitched from this form, newest first, so someone pitching several sees each one land
    const [pitched, setPitched] = useState<{ _id: string, title: string }[]>([]);
    const titleInput = React.useRef<HTMLInputElement | null>(null);
    // '' = pitched by you
    const [onBehalfOf, setOnBehalfOf] = useState('');
    // the editor only reads its initial value, so a new key is how the form gets cleared
    const [editorKey, setEditorKey] = useState(0);

    return (
        <form
            id="new-idea-form"
            style={{ scrollMarginTop: 88 }}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (submitting) {
                    return;
                }
                // a title is enough to get an idea down; the pitch can follow
                if (!idea.title.trim()) {
                    setError('Give your idea a title');
                    // the field itself is where the fix goes
                    titleInput.current?.focus();
                    return;
                }

                setSubmitting(true);
                setError('');
                createIdea(hackathonId, onBehalfOf ? { ...idea, onBehalfOf } : idea).then((data) => {
                    onCreate(data._id);
                    setPitched(list => [{ _id: data._id, title: data.title }, ...list]);
                    // ready for the next one, with the confirmation (above the title) in view
                    titleInput.current?.focus({ preventScroll: true });
                    document.getElementById('new-idea-form')?.scrollIntoView({ block: 'start', behavior: 'smooth' });

                    setIdea({
                        title: '',
                        description: template ?? ''
                    });
                    setEditorKey(key => key + 1);
                }, (err) => {
                    setError(err.response?.data.message || 'An error occurred');
                }).finally(() => setSubmitting(false));

            }}>
            <Stack spacing={2} direction={'column'}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <h2>Submit a new idea</h2>
                    <HelpLink guide="submit">Pitching and teams</HelpLink>
                </Stack>
                {
                    // the last few slots on a Free event: say so in place, before someone writes a pitch that can't be saved
                    allowance && allowance.used >= allowance.limit - 5 && allowance.used < allowance.limit && (
                        <Typography variant="body2" color="text.secondary" role="status">
                            {allowance.used} of {allowance.limit} ideas used (Free plan).
                            {upgradeUrl && <> <Link to={upgradeUrl}>Upgrade for unlimited ideas</Link>.</>}
                        </Typography>
                    )
                }
                {
                    allowance && allowance.used >= allowance.limit && (
                        <Alert severity="info" action={upgradeUrl && <Button color="inherit" size="small" component={Link} to={upgradeUrl}>Upgrade</Button>}>
                            This hackathon has {allowance.limit} ideas, the most on the Free plan.
                            {upgradeUrl ? ' Upgrade it to Pro for unlimited ideas, or delete one.' : ' Join one of the ideas above, or ask the organizers.'}
                        </Alert>
                    )
                }

                {
                    pitched.length > 0 && (
                        <Alert severity="success">
                            Pitched <Link to={`/h/${hackathonSlug}/idea/${pitched[0]._id}`}>{pitched[0].title}</Link>. It's in the list above; pitching doesn't
                            put you on its team, so click <b>Join team</b> on it if you'll work on it. Add another below, or you're done.
                            {pitched.length > 1 && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Also pitched: {pitched.slice(1).map((p, i) => <React.Fragment key={p._id}>{i > 0 && ', '}<Link to={`/h/${hackathonSlug}/idea/${p._id}`}>{p.title}</Link></React.Fragment>)}
                                </Typography>
                            )}
                        </Alert>
                    )
                }
                {
                    onBehalfOptions && onBehalfOptions.length > 0 && (
                        <TextField select size="small" label="Pitched by" value={onBehalfOf} sx={{ maxWidth: 360 }}
                            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                            helperText={onBehalfOf ? 'They become the idea\'s author. It doesn\'t put them on its team.' : undefined}
                            onChange={(e) => setOnBehalfOf(e.target.value)}>
                            <MenuItem value="">You</MenuItem>
                            {onBehalfOptions.map(p => <MenuItem key={p.email} value={p.email}>{p.username} ({p.email})</MenuItem>)}
                        </TextField>
                    )
                }
                <TextField label="Title" value={idea.title} inputRef={titleInput} inputProps={{ maxLength: 120 }}
                    error={error === 'Give your idea a title' && !idea.title.trim()}
                    helperText={error === 'Give your idea a title' && !idea.title.trim() ? 'Required: a title is enough to pitch' : idea.title.length > 80 ? `${idea.title.length}/120` : undefined}
                    onChange={(e) => setIdea(current => ({ ...current, title: e.target.value }))} />
                <Typography variant="body2" color="text.secondary">Pitch (optional for now): the problem, what you'd build, who you need. You can add it later with Edit.</Typography>
                <React.Suspense fallback={<div>Loading...</div>}>
                    <Editor key={editorKey} toolbar={true} value={idea.description} onChange={(value) => setIdea(current => ({ ...current, description: value }))} />
                </React.Suspense>
                <Stack direction={'row'} spacing={2}>
                    <Button type="submit" variant={'contained'} disabled={submitting || (!!allowance && allowance.used >= allowance.limit)}>{submitting ? 'Submitting…' : 'Submit'}</Button>
                    {
                        error && error !== 'Give your idea a title' && <Alert severity='error'>{error}</Alert>
                    }
                </Stack>


            </Stack>
        </form>
    )

}
